import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  Alert,
  Animated,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useMomentUpload } from '../utils/MomentUploadContext';

const RatingModal = ({ 
  visible, 
  onClose, 
  restaurant, 
  userPrivyDid,
  onRatingComplete,
  pointsEarned,
  navigation
}) => {
  const [rating, setRating] = useState(0);
  const [caption, setCaption] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showDiscoAnimation, setShowDiscoAnimation] = useState(false);
  
  const createMoment = useMutation(api.moments.createMoment);
  const { startUpload, updateProgress, completeUpload, failUpload } = useMomentUpload();

  const handleStarPress = (starRating) => {
    setRating(starRating);
  };

  const performUpload = async () => {
    try {
      updateProgress(20);

      // Create moment (20-100%)
      await createMoment({
        privyDid: userPrivyDid,
        restaurantId: restaurant._id,
        rating: rating,
        caption: caption.trim() || undefined,
        visibility: 'friends',
      });

      updateProgress(100);
      completeUpload();
    } catch (error) {
      console.error('Error submitting rating:', error);
      failUpload(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      Alert.alert('Rating Required', 'Please select a star rating before submitting.');
      return;
    }

    if (!userPrivyDid) {
      Alert.alert('Error', 'User not found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    // Start upload tracking with retry callback
    startUpload({
      restaurantName: restaurant?.name || 'Restaurant',
      restaurantId: restaurant?._id,
      retryCallback: performUpload,
    });

    // Show disco ball animation and close modal immediately
    setShowDiscoAnimation(true);
    setTimeout(() => {
      setShowDiscoAnimation(false);
      // Navigate to Social tab if navigation is provided
      if (navigation) {
        navigation.navigate('Tabs', { screen: 'Social' });
      }
      onRatingComplete();
    }, 2000); // Auto-close after 2 seconds

    // Continue upload in background
    performUpload();
  };

  const handleSkip = () => {
    Alert.alert(
      'Skip Rating?',
      'You can always rate this restaurant later from your profile.',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Skip', 
          style: 'destructive',
          onPress: onRatingComplete 
        }
      ]
    );
  };

  const getStarData = (starNumber) => {
    switch (starNumber) {
      case 1: return { emoji: '🫢', label: 'Toss' };
      case 2: return { emoji: '', label: '' };
      case 3: return { emoji: '😌', label: 'Solid' };
      case 4: return { emoji: '', label: '' };
      case 5: return { emoji: '🤤', label: 'Crave' };
      default: return { emoji: '⭐', label: '' };
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      const isFilled = starNumber <= rating;
      const starData = getStarData(starNumber);
      
      return (
        <TouchableOpacity
          key={starNumber}
          onPress={() => handleStarPress(starNumber)}
          style={styles.starColumn}
          disabled={isSubmitting}
        >
          <Ionicons
            name={isFilled ? 'star' : 'star-outline'}
            size={32}
            color={isFilled ? '#4e2916' : '#DDD'}
            style={styles.starIcon}
          />
          <Text style={styles.starEmoji}>{starData.emoji}</Text>
          <Text style={styles.starLabel}>{starData.label}</Text>
        </TouchableOpacity>
      );
    });
  };


  // Disco ball animation
  const discoScale = useRef(new Animated.Value(0)).current;
  const discoOpacity = useRef(new Animated.Value(0)).current;
  const sparkleOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (showDiscoAnimation) {
      // Reset animation values
      discoScale.setValue(0);
      discoOpacity.setValue(0);
      sparkleOpacity.setValue(0);
      
      // Start animation sequence
      Animated.parallel([
        Animated.timing(discoScale, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(discoOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();

      // Sparkles animation
      Animated.sequence([
        Animated.timing(sparkleOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(sparkleOpacity, {
          toValue: 0,
          duration: 1200,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Reset values when animation is hidden
      discoScale.setValue(0);
      discoOpacity.setValue(0);
      sparkleOpacity.setValue(0);
    }
  }, [showDiscoAnimation]);

  return (
    <>
      <Modal
        visible={visible && !showDiscoAnimation}
        animationType="slide"
        transparent={true}
        onRequestClose={onClose}
      >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.successMessage}>Check-in Successful! 🎉</Text>
            {pointsEarned && (
              <Text style={styles.pointsMessage}>+{pointsEarned} Disgo points</Text>
            )}
            <Text style={styles.restaurantName}>{restaurant?.name}</Text>
          </View>

          <View style={styles.captionContainer}>
            {!showCommentInput ? (
              <TouchableOpacity
                style={styles.commentButton}
                onPress={() => setShowCommentInput(true)}
                disabled={isSubmitting}
              >
                <Ionicons name="add-circle-outline" size={20} color="#4e2916" />
                <Text style={styles.commentButtonText}>Write a comment</Text>
              </TouchableOpacity>
            ) : (
              <View>
                <TextInput
                  style={[styles.captionInput, { color: '#000000' }]}
                  placeholder="what's the vibe?"
                  value={caption}
                  onChangeText={setCaption}
                  multiline
                  maxLength={200}
                  editable={!isSubmitting}
                  autoFocus
                />
                <Text style={styles.characterCount}>{caption.length}/200</Text>
              </View>
            )}
          </View>

          <View style={styles.starsContainer}>
            {renderStars()}
          </View>

          <Text style={styles.rateItText}>Rate It</Text>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.skipButton]}
              onPress={handleSkip}
              disabled={isSubmitting}
            >
              <Text style={styles.skipButtonText}>Skip</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button, 
                styles.submitButton,
                rating === 0 && styles.submitButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={isSubmitting || rating === 0}
            >
              <View style={styles.submitButtonContent}>
                <Text style={styles.submitButtonText}>
                  {isSubmitting ? 'Submitting...' : 'Submit Rating'}
                </Text>
                {isSubmitting && (
                  <ActivityIndicator 
                    size="small" 
                    color="#FFFFFF" 
                    style={styles.loadingSpinner}
                  />
                )}
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      </Modal>

      {/* Disco Ball Success Animation */}
      {showDiscoAnimation && (
        <Modal
          visible={showDiscoAnimation}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.discoOverlay}>
            <Animated.View
              style={[
                styles.discoContainer,
                {
                  opacity: discoOpacity,
                  transform: [{ scale: discoScale }]
                }
              ]}
            >
              {/* Sparkles behind disco ball */}
              <Animated.View
                style={[
                  styles.sparklesContainer,
                  {
                    opacity: sparkleOpacity,
                  }
                ]}
              >
                <Text style={styles.sparkle1}>✨</Text>
                <Text style={styles.sparkle2}>✨</Text>
                <Text style={styles.sparkle3}>✨</Text>
                <Text style={styles.sparkle4}>✨</Text>
                <Text style={styles.sparkle5}>✨</Text>
                <Text style={styles.sparkle6}>✨</Text>
              </Animated.View>
              
              <Text style={styles.discoBall}>🪩</Text>
              <Text style={styles.discoText}>Let's Disgo!</Text>
            </Animated.View>
          </View>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  successMessage: {
    fontSize: 18,
    fontWeight: '600',
    color: '#4e2916',
    marginBottom: 4,
  },
  pointsMessage: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4e2916',
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  restaurantName: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
  },
  starsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 16,
    gap: 16,
  },
  starColumn: {
    alignItems: 'center',
    padding: 8,
    minWidth: 60,
  },
  starIcon: {
    marginBottom: 8,
  },
  starEmoji: {
    fontSize: 24,
    marginBottom: 4,
  },
  starLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4e2916',
    textAlign: 'center',
  },
  rateItText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4e2916',
    textAlign: 'center',
    marginBottom: 24,
  },
  captionContainer: {
    marginBottom: 24,
  },
  commentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 12,
    backgroundColor: '#FAFAFA',
  },
  commentButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4e2916',
    marginLeft: 8,
  },
  captionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  captionInput: {
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 12,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
    backgroundColor: '#FAFAFA',
  },
  characterCount: {
    fontSize: 12,
    color: '#999',
    textAlign: 'right',
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  skipButton: {
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#DDD',
  },
  skipButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  submitButton: {
    backgroundColor: '#4e2916',
  },
  submitButtonDisabled: {
    backgroundColor: '#CCC',
  },
  submitButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingSpinner: {
    marginLeft: 8,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  discoOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  discoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  sparklesContainer: {
    position: 'absolute',
    width: 200,
    height: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sparkle1: {
    position: 'absolute',
    fontSize: 24,
    top: 20,
    left: 30,
  },
  sparkle2: {
    position: 'absolute',
    fontSize: 20,
    top: 40,
    right: 20,
  },
  sparkle3: {
    position: 'absolute',
    fontSize: 28,
    bottom: 60,
    left: 10,
  },
  sparkle4: {
    position: 'absolute',
    fontSize: 22,
    bottom: 30,
    right: 40,
  },
  sparkle5: {
    position: 'absolute',
    fontSize: 26,
    top: 80,
    left: 50,
  },
  sparkle6: {
    position: 'absolute',
    fontSize: 24,
    top: 100,
    right: 50,
  },
  discoBall: {
    fontSize: 120,
    marginBottom: 20,
    zIndex: 1,
  },
  discoText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#F9F5F3',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
});

export default RatingModal;
